import { vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { Series } from '~/domain/dicom/series';
import { validateSlicePositions } from '~/domain/patient/image3d';

export interface AssembledVolume {
    pixels: Int16Array;
    grid: Grid;
}

/**
 * Assemble a regular 3D volume from raw DICOM slices that may have
 * non-uniform spacing. Sorts slices by Z projection, detects the
 * base spacing, and fills gaps using nearest-neighbor duplication.
 *
 * This handles the case where `Image3D.fromSeries()` fails due to
 * ResamplingNeeded or excessive MissingSlices.
 *
 * @param series     The Series containing DicomImageSlice objects
 * @param zRangeMm   Optional [min, max] Z range in mm to crop
 * @returns Assembled volume with pixels and grid
 */
export function assembleVolumeFromSlices(
    series: Series,
    zRangeMm?: [number, number]
): AssembledVolume {
    const slices = series.dicomImageSlices;

    if (slices.length === 0) {
        throw new Error('Cannot assemble volume: no slices in series');
    }

    const exampleSlice = slices[0];

    // Get z-direction from image orientation
    const zDir = vec3.fromValues(
        exampleSlice.imageOrientation[6],
        exampleSlice.imageOrientation[7],
        exampleSlice.imageOrientation[8]
    );

    // Sort slices by Z projection
    const slicesWithZ = slices.map((slice) => ({
        slice,
        z: vec3.dot(zDir, slice.imagePosition),
    }));
    slicesWithZ.sort((a, b) => a.z - b.z);

    // Apply Z range filter if provided
    let filteredSlices = slicesWithZ;
    if (zRangeMm) {
        filteredSlices = slicesWithZ.filter(
            (s) => s.z >= zRangeMm[0] && s.z <= zRangeMm[1]
        );
        if (filteredSlices.length < 2) {
            throw new Error('Z range too narrow: fewer than 2 slices remain');
        }
    }

    // Detect base spacing using validation logic
    const validation = validateSlicePositions(series);
    let baseSpacing = validation.expectedZResolution;
    if (!baseSpacing || baseSpacing <= 0) {
        // Fallback: compute median gap
        const gaps: number[] = [];
        for (let i = 1; i < filteredSlices.length; i++) {
            gaps.push(Math.abs(filteredSlices[i].z - filteredSlices[i - 1].z));
        }
        gaps.sort((a, b) => a - b);
        baseSpacing =
            gaps.length > 0
                ? gaps[Math.floor(gaps.length / 2)]
                : exampleSlice.sliceThickness || 1.0;
    }

    const nx = exampleSlice.cols;
    const ny = exampleSlice.rows;

    // Validate that all slices share the same in-plane geometry.
    // Mismatched dimensions would silently corrupt the output volume.
    for (const { slice } of slicesWithZ) {
        if (slice.cols !== nx || slice.rows !== ny) {
            throw new Error(
                `Slice dimension mismatch: expected ${nx}x${ny}, ` +
                    `got ${slice.cols}x${slice.rows} ` +
                    `(SOP: ${slice.sopInstanceUid})`
            );
        }
    }

    // Compute output grid dimensions
    const minZ = filteredSlices[0].z;
    const maxZ = filteredSlices[filteredSlices.length - 1].z;
    const zExtent = maxZ - minZ;
    const nz = Math.max(1, Math.round(zExtent / baseSpacing) + 1);

    // Build grid using the bottom slice position and base spacing
    const bottomSlice = filteredSlices[0].slice;
    const resolution = vec3.fromValues(
        exampleSlice.pixelSpacing[0],
        exampleSlice.pixelSpacing[1],
        baseSpacing
    );
    const size = vec3.fromValues(nx, ny, nz);

    const grid = Grid.fromDicom(
        size,
        resolution,
        bottomSlice.imagePosition,
        exampleSlice.imageOrientation
    );

    // Allocate output volume
    const sliceSize = nx * ny;
    const pixels = new Int16Array(nx * ny * nz);

    // Map each output Z index to the nearest available slice
    // (nearest-neighbor fill for non-uniform gaps)
    for (let zi = 0; zi < nz; zi++) {
        const targetZ = minZ + zi * baseSpacing;

        // Find nearest slice by Z position
        let bestIdx = 0;
        let bestDist = Math.abs(filteredSlices[0].z - targetZ);
        for (let i = 1; i < filteredSlices.length; i++) {
            const dist = Math.abs(filteredSlices[i].z - targetZ);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }

        const srcSlice = filteredSlices[bestIdx].slice;
        const dstOffset = zi * sliceSize;
        pixels.set(srcSlice.pixelData, dstOffset);
    }

    return { pixels, grid };
}

/**
 * Compute the world-space axis-aligned bounding box of a set of
 * DICOM image slices.
 *
 * The AABB covers the full voxel extent (edge-to-edge), not just
 * voxel centers.  DICOM ImagePositionPatient is the center of the
 * first voxel, so we extend by half a pixel on each side.
 */
export function computeSliceAABB(slices: DicomImageSlice[]): {
    worldMin: vec3;
    worldMax: vec3;
} {
    if (slices.length === 0) {
        return {
            worldMin: vec3.create(),
            worldMax: vec3.create(),
        };
    }

    const worldMin = vec3.fromValues(+Infinity, +Infinity, +Infinity);
    const worldMax = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    for (const slice of slices) {
        const pos = slice.imagePosition;
        const orientation = slice.imageOrientation;
        const dx = slice.pixelSpacing[0];
        const dy = slice.pixelSpacing[1];
        const nx = slice.cols;
        const ny = slice.rows;

        // Row and column directions
        const rowDir = vec3.fromValues(
            orientation[0],
            orientation[1],
            orientation[2]
        );
        const colDir = vec3.fromValues(
            orientation[3],
            orientation[4],
            orientation[5]
        );

        // Voxel-center origin shifted to the corner of the first voxel
        const corner = vec3.scaleAndAdd(
            vec3.create(),
            pos as vec3,
            rowDir,
            -0.5 * dx
        );
        vec3.scaleAndAdd(corner, corner, colDir, -0.5 * dy);

        // Compute 4 edge-to-edge corners of this slice
        const corners = [
            vec3.clone(corner),
            vec3.scaleAndAdd(vec3.create(), corner, rowDir, nx * dx),
            vec3.scaleAndAdd(vec3.create(), corner, colDir, ny * dy),
            vec3.create(),
        ];
        vec3.scaleAndAdd(corners[3], corner, rowDir, nx * dx);
        vec3.scaleAndAdd(corners[3], corners[3], colDir, ny * dy);

        for (const c of corners) {
            vec3.min(worldMin, worldMin, c);
            vec3.max(worldMax, worldMax, c);
        }
    }

    return { worldMin, worldMax };
}

/**
 * Compute the median gap spacing from sorted slice Z positions.
 */
export function computeMedianSpacing(slices: DicomImageSlice[]): number {
    if (slices.length < 2) {
        return slices[0]?.sliceThickness || 1.0;
    }

    const zDir = vec3.fromValues(
        slices[0].imageOrientation[6],
        slices[0].imageOrientation[7],
        slices[0].imageOrientation[8]
    );

    const zPositions = slices
        .map((s) => vec3.dot(zDir, s.imagePosition))
        .sort((a, b) => a - b);

    const gaps: number[] = [];
    for (let i = 1; i < zPositions.length; i++) {
        gaps.push(Math.abs(zPositions[i] - zPositions[i - 1]));
    }
    gaps.sort((a, b) => a - b);

    return gaps[Math.floor(gaps.length / 2)];
}
